/**
 * Sentry Configuration
 * 
 * This file configures Sentry for error tracking, performance monitoring,
 * and session replay in the React application.
 * 
 * Best Practices:
 * - Initialize Sentry as early as possible in the application lifecycle
 * - Use environment variables for DSN and configuration
 * - Filter out sensitive data before sending to Sentry
 * - Set appropriate sample rates for production vs staging
 * - Integrate with React Error Boundaries
 */

import * as Sentry from "@sentry/react";
import config from "./env";

/**
 * Check if Sentry should be enabled
 */
const isSentryEnabled = () => {
  const enabled = process.env.REACT_APP_SENTRY_ENABLED === 'true';
  const hasDSN = !!process.env.REACT_APP_SENTRY_DSN;
  
  // Only enable Sentry if explicitly enabled and DSN is provided
  return enabled && hasDSN;
};

/**
 * Get Sentry configuration based on environment
 */
const getSentryConfig = () => {
  const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT || config.env;
  const tracesSampleRate = parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '0.1');
  const replaysSessionSampleRate = parseFloat(process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.01');
  const replaysOnErrorSampleRate = parseFloat(process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0');

  return {
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment,
    
    // Release tracking
    release: `${config.appName}@${config.appVersion}`,
    
    // Performance Monitoring
    tracesSampleRate,
    
    // Session Replay
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    
    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration({
        // Set tracePropagationTargets to control distributed tracing
        tracePropagationTargets: [
          "localhost",
          /^https:\/\/.*\.bitazar\.com/,
          process.env.REACT_APP_API_URL,
        ],
      }),

      // Session Replay for debugging
      Sentry.replayIntegration({
        // Mask all text content for privacy
        maskAllText: true,
        // Block all media (images, videos, etc.) for privacy
        blockAllMedia: true,
      }),

      // Note: React Router integration is removed from here because it requires
      // the history object which is not available at initialization time.
      // React Router tracing is handled by browserTracingIntegration above.
      
      // Capture console errors
      Sentry.captureConsoleIntegration({
        levels: ['error'],
      }),
    ],
    
    // Enable debug mode in development
    debug: config.isDevelopment,
    
    // Send default PII (Personally Identifiable Information)
    // Set to false in production for privacy
    sendDefaultPii: false,
    
    // Before send hook - filter and sanitize data
    beforeSend(event, hint) {
      // Filter out browser extension errors
      if (event.exception) {
        const exceptionValues = event.exception.values || [];
        for (const exception of exceptionValues) {
          const stacktrace = exception.stacktrace;
          if (stacktrace && stacktrace.frames) {
            const frames = stacktrace.frames;
            for (const frame of frames) {
              const filename = frame.filename || '';
              // Ignore errors from browser extensions
              if (
                filename.includes('chrome-extension://') ||
                filename.includes('moz-extension://') ||
                filename.includes('safari-extension://')
              ) {
                return null; // Don't send this event
              }
            }
          }
          
          // Filter out common noise
          const errorMessage = exception.value || '';
          if (
            errorMessage.includes('ResizeObserver loop') ||
            errorMessage.includes('Non-Error promise rejection') ||
            errorMessage.includes('disconnected port') ||
            errorMessage.includes('Extension context invalidated')
          ) {
            return null; // Don't send this event
          }
        }
      }
      
      // Remove sensitive data from event
      if (event.request) {
        // Remove authorization headers
        if (event.request.headers) {
          delete event.request.headers.Authorization;
          delete event.request.headers.authorization;
        }
        
        // Remove sensitive query parameters
        if (event.request.query_string) {
          const sensitiveParams = ['token', 'password', 'secret', 'api_key'];
          sensitiveParams.forEach(param => {
            if (event.request.query_string.includes(param)) {
              event.request.query_string = '[Filtered]';
            }
          });
        }
      }
      
      // Remove sensitive data from extra context
      if (event.extra) {
        const sensitiveKeys = ['password', 'token', 'secret', 'api_key', 'authorization'];
        sensitiveKeys.forEach(key => {
          if (event.extra[key]) {
            event.extra[key] = '[Filtered]';
          }
        });
      }
      
      return event;
    },
    
    // Ignore specific errors
    ignoreErrors: [
      // Browser extension errors
      'chrome-extension://',
      'moz-extension://',
      'safari-extension://',
      
      // Network errors that are expected
      'Network request failed',
      'NetworkError',
      'Failed to fetch',
      
      // ResizeObserver errors (common and harmless)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      
      // Common React errors that are handled
      'ChunkLoadError',
      'Loading chunk',
      
      // Third-party script errors
      'Script error',
      
      // Cancelled requests
      'Request aborted',
      'AbortError',
    ],
    
    // Deny URLs - don't track errors from these sources
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      
      // Third-party scripts
      /google-analytics\.com/i,
      /googletagmanager\.com/i,
      /facebook\.net/i,
      /connect\.facebook\.net/i,
    ],
  };
};

/**
 * Initialize Sentry
 */
export const initializeSentry = () => {
  if (!isSentryEnabled()) {
    console.log('🔇 Sentry is disabled');
    return;
  }

  try {
    const sentryConfig = getSentryConfig();
    Sentry.init(sentryConfig);
    
    console.log(`✅ Sentry initialized for ${sentryConfig.environment} environment`);
    console.log(`📊 Traces sample rate: ${sentryConfig.tracesSampleRate * 100}%`);
    console.log(`🎥 Replay session sample rate: ${sentryConfig.replaysSessionSampleRate * 100}%`);
  } catch (error) {
    console.error('❌ Failed to initialize Sentry:', error);
  }
};

/**
 * Set user context in Sentry
 * Call this after user logs in
 */
export const setSentryUser = (user) => {
  if (!isSentryEnabled()) return;
  
  try {
    Sentry.setUser({
      id: user.id || user.user_id,
      email: user.email,
      username: user.username || user.name,
      // Add custom user properties
      level: user.level,
      kyc_status: user.kyc_status,
    });
    
    console.log('👤 Sentry user context set');
  } catch (error) {
    console.error('❌ Failed to set Sentry user:', error);
  }
};

/**
 * Clear user context in Sentry
 * Call this after user logs out
 */
export const clearSentryUser = () => {
  if (!isSentryEnabled()) return;
  
  try {
    Sentry.setUser(null);
    console.log('👤 Sentry user context cleared');
  } catch (error) {
    console.error('❌ Failed to clear Sentry user:', error);
  }
};

/**
 * Add breadcrumb to Sentry
 * Useful for tracking user actions leading up to an error
 */
export const addSentryBreadcrumb = (message, category = 'custom', level = 'info', data = {}) => {
  if (!isSentryEnabled()) return;
  
  try {
    Sentry.addBreadcrumb({
      message,
      category,
      level,
      data,
      timestamp: Date.now() / 1000,
    });
  } catch (error) {
    console.error('❌ Failed to add Sentry breadcrumb:', error);
  }
};

/**
 * Capture exception manually
 */
export const captureSentryException = (error, context = {}) => {
  if (!isSentryEnabled()) return;
  
  try {
    Sentry.captureException(error, {
      extra: context,
    });
  } catch (err) {
    console.error('❌ Failed to capture Sentry exception:', err);
  }
};

/**
 * Capture message manually
 */
export const captureSentryMessage = (message, level = 'info', context = {}) => {
  if (!isSentryEnabled()) return;
  
  try {
    Sentry.captureMessage(message, {
      level,
      extra: context,
    });
  } catch (error) {
    console.error('❌ Failed to capture Sentry message:', error);
  }
};

/**
 * Set custom context/tags
 */
export const setSentryContext = (contextName, contextData) => {
  if (!isSentryEnabled()) return;
  
  try {
    Sentry.setContext(contextName, contextData);
  } catch (error) {
    console.error('❌ Failed to set Sentry context:', error);
  }
};

export const setSentryTag = (key, value) => {
  if (!isSentryEnabled()) return;
  
  try {
    Sentry.setTag(key, value);
  } catch (error) {
    console.error('❌ Failed to set Sentry tag:', error);
  }
};

// Export Sentry for direct use if needed
export { Sentry };

export default {
  initializeSentry,
  setSentryUser,
  clearSentryUser,
  addSentryBreadcrumb,
  captureSentryException,
  captureSentryMessage,
  setSentryContext,
  setSentryTag,
  Sentry,
};

